import { Component, OnInit, OnDestroy, ChangeDetectorRef, ViewChild, Input } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MenuItem, MessageService, SortEvent } from 'primeng/api';
import { Menu } from 'primeng/menu';
import { RucBuscaComponent } from '../../shared/components/ruc-busca/ruc-busca.component';
import { debounceTime, Subject, Subscription, takeUntil } from 'rxjs';
import { PessoaRucResultado } from '../../models/pessoas/pessoa-ruc-resultado.model';
import { PessoaService } from '../../services/pessoas/pessoas.service';
import { CidadeService } from '../../services/configuracoes/cidade.service';
import { PessoaDTO, PessoaFormDTO } from '../../models/clientes/pessoa.model';
import { TipoPessoa } from '../../enums/tipoPessoa.enum';
import { TipoNaturezaFiscal } from '../../enums/tipoNaturezaFiscal.enum';
import { Sexo } from '../../enums/sexo.enum';
import { TableColumn } from '../../models/shared/table-column.model';
import { PaisDDI } from '../../models/cardapio-digital/cardapio-digital.model';
import { PAISES_DDI } from '../../shared/data/paises-ddi.data';
import { filtrarRucDoEvento, isDvRucValido } from '../../shared/utils/ruc.util';

@Component({
  selector: 'app-fornecedores',
  templateUrl: './fornecedores.component.html',
  styleUrls: ['./fornecedores.component.scss'],
  providers: [ConfirmationService, MessageService],
})
export class FornecedoresComponent implements OnInit, OnDestroy {
  /**
   * Modo EMBUTIDO: reusa a tela dentro do Operar Cliente (aba "Gestão de
   * fornecedores"), sob o contexto do cliente. Esconde o cabeçalho da página e as
   * ações que saem do contexto (excluir + menu extra).
   */
  @Input() embutido = false;

  // Controle de abas (0 = Cadastro, 1 = Lista)
  activeTabIndex: number = 1;

  // Estado do formulario
  form: PessoaFormDTO = this.createEmptyForm();
  cidadeList: { label: string; value: number; estado: string }[] = [];
  estadoCidade: string = '';
  tipoPessoaOptions: { value: TipoPessoa; label: string }[] = [];
  tipoNaturezaFiscalOptions: { value: TipoNaturezaFiscal; label: string }[] = [];
  sexoOptions: { value: Sexo; label: string }[] = [];
  isFormSubmitted: boolean = false;

  // DDI dropdown
  paisSelecionado: PaisDDI = PAISES_DDI[0];

  // Dados da tabela
  pessoas: any[] = [];
  pessoasOriginal: any[] = [];
  first3: number = 0;
  rows3: number = 10;
  /** Guarda o ultimo valor de "rows3" processado pelo onRowsPerPageChange,
   *  para evitar chamadas duplicadas de findFornecedores quando o (ngModelChange)
   *  do dropdown de itens por pagina dispara no bootstrap com o valor atual. */
  private _lastRows: number = 10;
  totalRecords: number = 0;
  loading: boolean = true;
  searchNome: string = '';
  private searchSubject$ = new Subject<string>();
  lastfield: string = 'id';
  lastSort: string = 'asc';

  pageSizeOptions = [
    { label: 5, value: 5 },
    { label: 10, value: 10 },
    { label: 20, value: 20 },
    { label: 120, value: 120 },
  ];

  // Dialog de exclusao
  deleteDialogVisible = false;
  deleteItemId: string | null = null;

  // Busca automatica por CI
  private ciInput$ = new Subject<string>();
  private ciDestroy$ = new Subject<void>();

  // Dialog pessoa ja cadastrada
  pessoaJaCadastradaDialogVisible = false;
  pessoaJaCadastradaMensagem = '';
  pessoaJaCadastradaId: number | null = null;

  // Configuracao de colunas visiveis
  readonly STORAGE_KEY = 'fornecedores-visible-columns';

  columns: TableColumn[] = [
    { key: 'codigoPessoa', labelKey: 'fornecedores.labels.codigo_fornecedor', visible: true, fixed: true, defaultVisible: true },
    { key: 'nome', labelKey: 'fornecedores.labels.nome', visible: true, fixed: false, defaultVisible: true },
    { key: 'fantasia', labelKey: 'fornecedores.labels.nome_fantasia', visible: true, fixed: false, defaultVisible: true },
    { key: 'telefone', labelKey: 'fornecedores.labels.telefone', visible: true, fixed: false, defaultVisible: true },
    { key: 'ci', labelKey: 'fornecedores.labels.ci', visible: true, fixed: false, defaultVisible: true },
    { key: 'ruc', labelKey: 'fornecedores.labels.ruc', visible: true, fixed: false, defaultVisible: true },
    { key: 'cidade', labelKey: 'fornecedores.labels.cidade', visible: true, fixed: false, defaultVisible: true },
    { key: 'criadoEm', labelKey: 'fornecedores.labels.data_inclusao', visible: true, fixed: false, defaultVisible: true },
    { key: 'acoes', labelKey: 'fornecedores.labels.acoes', visible: true, fixed: true, defaultVisible: true },
  ];

  // Objetos de traducao (padrao moderno - tipo literal implicito)
  texts = {
    titulo: '',
    subtitulo: '',
    titulo_criar: '',
    subtitulo_criar: '',
    titulo_editar: '',
    subtitulo_editar: '',
    identificacao: '',
    contato: '',
    dados_pessoais: '',
    endereco: '',
    lista_de_fornecedores: '',
    nenhum_fornecedor: '',
    ajuste_filtros: '',
    carregando: '',
    confirmar_exclusao: '',
    confirmacao: '',
    fornecedor_criado_sucesso: '',
    fornecedor_atualizado_sucesso: '',
    erro_ao_criar_fornecedor: '',
    erro_ao_atualizar_fornecedor: '',
    fornecedor_excluido_sucesso: '',
    erro_ao_excluir_fornecedor: '',
    data_nascimento_invalida: '',
    campos_obrigatorios: '',
    aba_cadastro: '',
    aba_lista: ''
  };

  labels = {
    ruc: '', ci: '', nome: '', nome_fantasia: '',
    tipo_pessoa: '', tipo_natureza_fiscal: '',
    categoria: '', cliente: '', provedor: '',
    ddi: '', telefone: '', email: '',
    data_nascimento: '', sexo: '',
    cidade: '', estado: '', endereco: '', numero: '',
    codigo_fornecedor: '', data_inclusao: '', acoes: '',
    itens_por_pagina: ''
  };

  placeholders = {
    ruc: '', ci: '', nome: '', nome_fantasia: '',
    tipo_pessoa: '', tipo_natureza_fiscal: '',
    ddi: '', telefone: '', email: '',
    sexo: '', cidade: '',
    endereco: '', numero: '',
    pesquisar: ''
  };

  botoes = {
    salvar: '', atualizar: '', limpar: '',
    novo_fornecedor: '',
    confirmar: '', cancelar: ''
  };

  tooltips = {
    visualizar: '', editar: '', excluir: '',
    adicionar_despesa: '', adicionar_receita: '',
    ver_pedidos: '', ver_titulos_abertos: '', ver_itens: '', mais_acoes: ''
  };

  @ViewChild('menuAcoes') menuAcoes!: Menu;
  @ViewChild('menuAcoesMobile') menuAcoesMobile!: Menu;
  @ViewChild('rucBuscaRef') rucBuscaRef?: RucBuscaComponent;
  menuItems: MenuItem[] = [];
  menuItemsMobile: MenuItem[] = [];

  private langChangeSub!: Subscription;

  constructor(
    private translate: TranslateService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private pessoaService: PessoaService,
    private cidadeService: CidadeService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private route: ActivatedRoute
  ) { }

  ngOnInit(): void {
    this.loadColumnPreferences();
    this.findCidades();
    this.findFornecedores(0, this.rows3);
    this.loadTranslations();
    this.buildTipoPessoaOptions();
    this.buildTipoNaturezaFiscalOptions();
    this.buildSexoOptions();
    this.setDefaultDropdownValues();

    // Inicializar debounce de busca por CI
    this.inicializarDebounceCI();

    // Debounce da busca de fornecedores (400ms)
    this.searchSubject$.pipe(
      debounceTime(400),
      takeUntil(this.ciDestroy$)
    ).subscribe(term => {
      this.buscarFornecedoresNoBackend(term);
    });

    // Verificar query param ?edit= para abrir edicao direta
    this.route.queryParams.subscribe(params => {
      const editId = params['edit'];
      if (editId) {
        this.editarFornecedor(Number(editId));
      }
    });

    // Monitorar mudanca de idioma
    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
      this.buildTipoPessoaOptions();
      this.buildTipoNaturezaFiscalOptions();
      this.buildSexoOptions();
      this.setDefaultDropdownValues();
    });
  }

  ngOnDestroy(): void {
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
    this.ciDestroy$.next();
    this.ciDestroy$.complete();
  }

  // =========================================================================
  // Traducoes
  // =========================================================================

  onTabChange(event: { index: number }): void {
    if (event.index === 1) {
      this.texts.titulo = this.translate.instant('fornecedores.texts.titulo_criar');
      this.texts.subtitulo = this.translate.instant('fornecedores.texts.subtitulo_lista');
    } else {
      const isEdicao = !!this.form.id;
      this.texts.titulo = this.translate.instant(
        isEdicao ? 'fornecedores.texts.titulo_editar' : 'fornecedores.texts.titulo_criar'
      );
      this.texts.subtitulo = this.translate.instant(
        isEdicao ? 'fornecedores.texts.subtitulo_editar' : 'fornecedores.texts.subtitulo_criar'
      );
    }
  }

  private loadTranslations(): void {
    const isEdicao = !!this.form.id;
    const isListaAtiva = this.activeTabIndex === 1;

    this.texts = {
      titulo: this.translate.instant(
        isListaAtiva ? 'fornecedores.texts.titulo_criar' : isEdicao ? 'fornecedores.texts.titulo_editar' : 'fornecedores.texts.titulo_criar'
      ),
      subtitulo: this.translate.instant(
        isListaAtiva
          ? 'fornecedores.texts.subtitulo_lista'
          : isEdicao ? 'fornecedores.texts.subtitulo_editar' : 'fornecedores.texts.subtitulo_criar'
      ),
      titulo_criar: this.translate.instant('fornecedores.texts.titulo_criar'),
      subtitulo_criar: this.translate.instant('fornecedores.texts.subtitulo_criar'),
      titulo_editar: this.translate.instant('fornecedores.texts.titulo_editar'),
      subtitulo_editar: this.translate.instant('fornecedores.texts.subtitulo_editar'),
      identificacao: this.translate.instant('fornecedores.texts.identificacao'),
      contato: this.translate.instant('fornecedores.texts.contato'),
      dados_pessoais: this.translate.instant('fornecedores.texts.dados_pessoais'),
      endereco: this.translate.instant('fornecedores.texts.endereco'),
      lista_de_fornecedores: this.translate.instant('fornecedores.texts.lista_de_fornecedores'),
      nenhum_fornecedor: this.translate.instant('fornecedores.texts.nenhum_fornecedor'),
      ajuste_filtros: this.translate.instant('fornecedores.texts.ajuste_filtros'),
      carregando: this.translate.instant('fornecedores.texts.carregando'),
      confirmar_exclusao: this.translate.instant('fornecedores.texts.confirmar_exclusao'),
      confirmacao: this.translate.instant('fornecedores.texts.confirmacao'),
      fornecedor_criado_sucesso: this.translate.instant('fornecedores.texts.fornecedor_criado_sucesso'),
      fornecedor_atualizado_sucesso: this.translate.instant('fornecedores.texts.fornecedor_atualizado_sucesso'),
      erro_ao_criar_fornecedor: this.translate.instant('fornecedores.texts.erro_ao_criar_fornecedor'),
      erro_ao_atualizar_fornecedor: this.translate.instant('fornecedores.texts.erro_ao_atualizar_fornecedor'),
      fornecedor_excluido_sucesso: this.translate.instant('fornecedores.texts.fornecedor_excluido_sucesso'),
      erro_ao_excluir_fornecedor: this.translate.instant('fornecedores.texts.erro_ao_excluir_fornecedor'),
      data_nascimento_invalida: this.translate.instant('fornecedores.texts.data_nascimento_invalida'),
      campos_obrigatorios: this.translate.instant('fornecedores.texts.campos_obrigatorios'),
      aba_cadastro: this.translate.instant('fornecedores.texts.aba_cadastro'),
      aba_lista: this.translate.instant('fornecedores.texts.aba_lista')
    };

    this.labels = {
      ruc: this.translate.instant('fornecedores.labels.ruc'),
      ci: this.translate.instant('fornecedores.labels.ci'),
      nome: this.translate.instant('fornecedores.labels.nome'),
      nome_fantasia: this.translate.instant('fornecedores.labels.nome_fantasia'),
      tipo_pessoa: this.translate.instant('fornecedores.labels.tipo_pessoa'),
      tipo_natureza_fiscal: this.translate.instant('fornecedores.labels.tipo_natureza_fiscal'),
      categoria: this.translate.instant('fornecedores.labels.categoria'),
      cliente: this.translate.instant('fornecedores.labels.cliente'),
      provedor: this.translate.instant('fornecedores.labels.provedor'),
      ddi: this.translate.instant('fornecedores.labels.ddi'),
      telefone: this.translate.instant('fornecedores.labels.telefone'),
      email: this.translate.instant('fornecedores.labels.email'),
      data_nascimento: this.translate.instant('fornecedores.labels.data_nascimento'),
      sexo: this.translate.instant('fornecedores.labels.sexo'),
      cidade: this.translate.instant('fornecedores.labels.cidade'),
      estado: this.translate.instant('fornecedores.labels.estado'),
      endereco: this.translate.instant('fornecedores.labels.endereco'),
      numero: this.translate.instant('fornecedores.labels.numero'),
      codigo_fornecedor: this.translate.instant('fornecedores.labels.codigo_fornecedor'),
      data_inclusao: this.translate.instant('fornecedores.labels.data_inclusao'),
      acoes: this.translate.instant('fornecedores.labels.acoes'),
      itens_por_pagina: this.translate.instant('fornecedores.labels.itens_por_pagina')
    };

    this.placeholders = {
      ruc: this.translate.instant('fornecedores.placeholders.ruc'),
      ci: this.translate.instant('fornecedores.placeholders.ci'),
      nome: this.translate.instant('fornecedores.placeholders.nome'),
      nome_fantasia: this.translate.instant('fornecedores.placeholders.nome_fantasia'),
      tipo_pessoa: this.translate.instant('fornecedores.placeholders.tipo_pessoa'),
      tipo_natureza_fiscal: this.translate.instant('fornecedores.placeholders.tipo_natureza_fiscal'),
      ddi: this.translate.instant('fornecedores.placeholders.ddi'),
      telefone: this.translate.instant('fornecedores.placeholders.telefone'),
      email: this.translate.instant('fornecedores.placeholders.email'),
      sexo: this.translate.instant('fornecedores.placeholders.sexo'),
      cidade: this.translate.instant('fornecedores.placeholders.cidade'),
      endereco: this.translate.instant('fornecedores.placeholders.endereco'),
      numero: this.translate.instant('fornecedores.placeholders.numero'),
      pesquisar: this.translate.instant('fornecedores.placeholders.pesquisar')
    };

    this.botoes = {
      salvar: this.translate.instant('fornecedores.botoes.salvar'),
      atualizar: this.translate.instant('fornecedores.botoes.atualizar'),
      limpar: this.translate.instant('fornecedores.botoes.limpar'),
      novo_fornecedor: this.translate.instant('fornecedores.botoes.novo_fornecedor'),
      confirmar: this.translate.instant('fornecedores.botoes.confirmar'),
      cancelar: this.translate.instant('fornecedores.botoes.cancelar')
    };

    this.tooltips = {
      visualizar: this.translate.instant('fornecedores.tooltips.visualizar'),
      editar: this.translate.instant('fornecedores.tooltips.editar'),
      excluir: this.translate.instant('fornecedores.tooltips.excluir'),
      adicionar_despesa: this.translate.instant('fornecedores.tooltips.adicionar_despesa'),
      adicionar_receita: this.translate.instant('fornecedores.tooltips.adicionar_receita'),
      ver_pedidos: this.translate.instant('fornecedores.tooltips.ver_pedidos'),
      ver_titulos_abertos: this.translate.instant('fornecedores.tooltips.ver_titulos_abertos'),
      ver_itens: this.translate.instant('fornecedores.tooltips.ver_itens'),
      mais_acoes: this.translate.instant('fornecedores.tooltips.mais_acoes')
    };
  }

  // =========================================================================
  // Opcoes de dropdown
  // =========================================================================

  private buildTipoPessoaOptions(): void {
    this.tipoPessoaOptions = Object.values(TipoPessoa).map(tipo => ({
      value: tipo,
      label: this.translate.instant(`enums.tipo_pessoa.${tipo.toLowerCase()}`)
    }));
  }

  private buildTipoNaturezaFiscalOptions(): void {
    this.tipoNaturezaFiscalOptions = Object.values(TipoNaturezaFiscal).map(natureza => ({
      value: natureza,
      label: this.translate.instant(`enums.tipo_natureza_fiscal.${natureza.toLowerCase()}`)
    }));
  }

  private buildSexoOptions(): void {
    this.sexoOptions = Object.values(Sexo).map(sexo => ({
      value: sexo,
      label: this.translate.instant(`enums.sexo.${sexo.toLowerCase()}`)
    }));
  }

  private setDefaultDropdownValues(): void {
    const tipoPessoaJuridica = this.tipoPessoaOptions.find(o => o.value === TipoPessoa.JURIDICA);
    if (tipoPessoaJuridica && !this.form.tipoPessoa) {
      this.form.tipoPessoa = tipoPessoaJuridica;
    }

    const contribuinte = this.tipoNaturezaFiscalOptions.find(o => o.value === TipoNaturezaFiscal.CONTRIBUINTE);
    if (contribuinte && !this.form.tipoNaturezaFiscal) {
      this.form.tipoNaturezaFiscal = contribuinte;
    }

    const masculino = this.sexoOptions.find(o => o.value === Sexo.MASCULINO);
    if (masculino && !this.form.sexo) {
      this.form.sexo = masculino;
    }
  }

  findCidades(): Promise<void> {
    return new Promise((resolve) => {
      this.cidadeService.get('/cidade?listarTodos=true').subscribe((response) => {
        this.cidadeList = response.content.map((cidade: any) => ({
          label: cidade.nome,
          value: Number(cidade.id),
          estado: cidade.nomeEstado
        }));
        resolve();
      });
    });
  }

  // =========================================================================
  // Formulario
  // =========================================================================

  createEmptyForm(): PessoaFormDTO {
    return {
      nome: '',
      fantasia: undefined,
      ruc: '',
      ci: '',
      email: '',
      ddi: '',
      telefone: '',
      endereco: '',
      numero: '',
      cidade: undefined,
      categoriaCliente: false,
      categoriaFornecedor: true,
      categoriaVendedor: false,
      tipoPessoa: undefined,
      tipoNaturezaFiscal: undefined,
      dataNascimento: '',
      sexo: undefined,
    };
  }

  onSelecionarCidade(event: any): void {
    // O showClear do p-dropdown emite onChange com value null: sem cidade, o estado tambem some.
    const valorCidade = event?.value?.value;
    if (valorCidade == null) {
      this.estadoCidade = '';
      return;
    }
    const cidadeSelecionada = this.cidadeList.find(cidade => cidade.value === valorCidade);
    if (cidadeSelecionada) {
      this.estadoCidade = cidadeSelecionada.estado;
    }
  }

  onDateInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const valor = input.value;
    if (valor) {
      const partes = valor.split('/');
      if (partes.length === 3 && partes[2].length > 4) {
        partes[2] = partes[2].substring(0, 4);
        const novaData = partes.join('/');
        input.value = novaData;
        this.form.dataNascimento = novaData;
      }
    }
  }

  validarDataNascimento(): boolean {
    if (!this.form.dataNascimento) {
      return true;
    }
    const partes = this.form.dataNascimento.split('/');
    if (partes.length === 3 && partes[2].length > 4) {
      return false;
    }
    return true;
  }

  /**
   * Aviso de DV invalido no RUC (modo edicao). So orienta: o cadastro e
   * salvo mesmo com o DV errado.
   */
  get avisoRucEdicao(): string | null {
    return isDvRucValido(this.form.ruc) === false
      ? this.translate.instant('ruc_busca.erros.dv_invalido')
      : null;
  }

  /** Sanitiza o campo RUC no modo edicao: mesma regra do app (digitos, um hifen, um DV) */
  onRucEdicaoInput(event: Event): void {
    this.form.ruc = filtrarRucDoEvento(event);
  }

  onNomeBlur(): void {
    if (this.form.nome && !this.form.fantasia) {
      this.form.fantasia = this.form.nome;
    }
  }

  onTipoPessoaChange(): void {
    if (this.form.tipoPessoa?.value === TipoPessoa.FISICA) {
      this.form.ruc = '';
    } else if (this.form.tipoPessoa?.value === TipoPessoa.JURIDICA) {
      this.form.ci = '';
    }
  }

  isPessoaFisica(): boolean {
    return this.form.tipoPessoa?.value === TipoPessoa.FISICA;
  }

  submit(): void {
    this.isFormSubmitted = true;

    if (!this.form.tipoPessoa || !this.form.tipoNaturezaFiscal || (this.isPessoaFisica() && !this.form.sexo) || !this.form.nome) {
      this.messageService.add({
        severity: 'error',
        summary: this.texts.confirmacao,
        detail: this.texts.campos_obrigatorios,
      });
      return;
    }


    if (!this.validarDataNascimento()) {
      this.messageService.add({
        severity: 'error',
        summary: this.texts.confirmacao,
        detail: this.texts.data_nascimento_invalida,
      });
      return;
    }

    // Limpar telefone: salvar apenas digitos puros (sem mascara)
    const telefoneLimpo = this.form.telefone?.replace(/\D/g, '') || undefined;

    // dataNascimento ja vem no formato dd/MM/yyyy do p-inputMask
    const dataNascimentoFormatada = this.form.dataNascimento || undefined;

    const body: PessoaDTO = {
      nome: this.form.nome,
      fantasia: this.form.fantasia || undefined,
      ruc: this.form.ruc || undefined,
      ci: this.form.ci || undefined,
      email: this.form.email || '',
      ddi: this.paisSelecionado.ddi || undefined,
      telefone: telefoneLimpo,
      endereco: this.form.endereco || undefined,
      numero: this.form.numero || undefined,
      cidade_id: this.form.cidade?.value ? Number(this.form.cidade.value) : undefined,
      categoriaCliente: this.form.categoriaCliente,
      categoriaFornecedor: this.form.categoriaFornecedor,
      categoriaVendedor: this.form.categoriaVendedor ?? false,
      tipoPessoa: this.form.tipoPessoa?.value,
      tipoNaturezaFiscal: this.form.tipoNaturezaFiscal?.value,
      dataNascimento: dataNascimentoFormatada,
      sexo: this.form.sexo?.value,
    };

    if (this.form.id) {
      // Modo edicao - PUT
      this.pessoaService.put(`/pessoas/${this.form.id}`, body).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: this.texts.confirmacao,
            detail: this.texts.fornecedor_atualizado_sucesso,
          });
          this.limparFormulario();
          this.findFornecedores(0, this.rows3, `${this.lastfield},${this.lastSort}`);
          this.activeTabIndex = 1; // Trocar para aba de lista
        },
        error: () => {
          // O HttpErrorInterceptor global já exibe o toast com a mensagem do backend.
        },
      });
    } else {
      // Modo criacao - POST
      this.pessoaService.post('/pessoas', body).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: this.texts.confirmacao,
            detail: this.texts.fornecedor_criado_sucesso,
          });
          this.limparFormulario();
          this.findFornecedores(0, this.rows3, `${this.lastfield},${this.lastSort}`);
          this.activeTabIndex = 1; // Trocar para aba de lista
        },
        error: (err: any) => {
          // O HttpErrorInterceptor global já exibe o toast com a mensagem do backend.
          // Apenas logar para debugging.
          console.error('Erro ao cadastrar fornecedor:', err);
        },
      });
    }
  }

  async editarFornecedor(id: number): Promise<void> {
    // Garantir que cidades estejam carregadas antes de popular o formulario
    if (this.cidadeList.length === 0) {
      await this.findCidades();
    }

    this.pessoaService.getById('/pessoas', String(id)).subscribe((response: any) => {
      const cidadeId = response.cidade?.id;
      const cidadeEncontrada = cidadeId ? this.cidadeList.find(c => c.value === Number(cidadeId)) : undefined;

      this.form = {
        id: response.id,
        nome: response.nome,
        fantasia: response.fantasia,
        ruc: response.ruc,
        ci: response.ci,
        email: response.email,
        ddi: response.ddi,
        telefone: response.telefone,
        endereco: response.endereco,
        numero: response.numero,
        categoriaCliente: Boolean(response.categoriaCliente),
        categoriaFornecedor: Boolean(response.categoriaFornecedor),
        categoriaVendedor: response.categoriaVendedor,
        cidade: cidadeEncontrada,
        tipoPessoa: this.tipoPessoaOptions.find(
          item => item.value === (response.tipoPessoa?.enum || response.tipoPessoa)
        ),
        tipoNaturezaFiscal: this.tipoNaturezaFiscalOptions.find(
          item => item.value === (response.tipoNaturezaFiscal?.enum || response.tipoNaturezaFiscal)
        ),
        dataNascimento: response.dataNascimento || '',
        sexo: this.sexoOptions.find(
          item => item.value === (response.sexo?.enum || response.sexo)
        ),
      };
      this.estadoCidade = response.cidade?.estado?.nome;

      // Converter DDI do backend para PaisDDI
      const ddiComPlus = response.ddi?.startsWith('+') ? response.ddi : `+${response.ddi || '595'}`;
      this.paisSelecionado = PAISES_DDI.find(p => p.ddi === ddiComPlus) || PAISES_DDI[0];

      // Resetar estado de validacao ao carregar dados para edicao
      this.isFormSubmitted = false;

      // Atualizar titulo para modo edicao
      this.texts.titulo = this.translate.instant('fornecedores.texts.titulo_editar');
      this.texts.subtitulo = this.translate.instant('fornecedores.texts.subtitulo_editar');

      // Trocar para aba de cadastro com os dados preenchidos
      this.activeTabIndex = 0;

      // Forcar deteccao de mudancas para p-inputMask atualizar
      this.cdr.detectChanges();

      // Scroll para o topo para ver o formulario preenchido
      this.scrollToFormulario();
    });
  }

  limparFormulario(): void {
    this.form = this.createEmptyForm();
    this.estadoCidade = '';
    this.isFormSubmitted = false;
    this.paisSelecionado = PAISES_DDI[0];
    this.rucBuscaRef?.reset();
    this.setDefaultDropdownValues();

    // Voltar titulo para modo criacao
    this.texts.titulo = this.translate.instant('fornecedores.texts.titulo_criar');
    this.texts.subtitulo = this.translate.instant('fornecedores.texts.subtitulo_criar');
  }

  novoFornecedor(): void {
    this.limparFormulario();
    this.activeTabIndex = 0; // Trocar para aba de cadastro
    this.scrollToFormulario();
  }

  scrollToFormulario(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // =========================================================================
  // Busca automatica RUC / CI
  // =========================================================================

  onRucPessoaConfirmada(resultado: PessoaRucResultado): void {
    this.form.ruc = resultado.ruc || '';
    if (resultado.nome) this.form.nome = resultado.nome;
    if (resultado.nome && !this.form.fantasia) this.form.fantasia = resultado.nome;
    if (resultado.ddi) {
      const ddiComPlus = resultado.ddi.startsWith('+') ? resultado.ddi : `+${resultado.ddi}`;
      const paisEncontrado = PAISES_DDI.find(p => p.ddi === ddiComPlus);
      if (paisEncontrado) this.paisSelecionado = paisEncontrado;
    }
    if (resultado.telefone) this.form.telefone = resultado.telefone;
    if (resultado.cidade?.id) {
      const cidadeEncontrada = this.cidadeList.find(c => c.value === Number(resultado.cidade!.id));
      if (cidadeEncontrada) {
        this.form.cidade = { label: cidadeEncontrada.label, value: Number(cidadeEncontrada.value) };
        this.estadoCidade = cidadeEncontrada.estado;
      }
    }
  }

  onRucPessoaVinculada(pessoa: PessoaDTO): void {
    this.pessoaJaCadastradaId = pessoa.id ?? null;
    this.pessoaJaCadastradaMensagem = this.translate.instant('fornecedores.texts.pessoa_ja_cadastrada', { nome: pessoa.nome });
    this.pessoaJaCadastradaDialogVisible = true;
  }

  onRucLimpo(): void {
    this.form.nome = '';
    this.form.fantasia = undefined;
    this.form.telefone = '';
    this.form.cidade = undefined;
    this.estadoCidade = '';
    this.paisSelecionado = PAISES_DDI[0];
  }

  onCiInput(event: Event): void {
    if (this.form.id) return;
    const input = event.target as HTMLInputElement;
    const valor = (input.value || '').trim();
    if (!valor || valor.length < 3) return;
    this.ciInput$.next(valor);
  }

  private inicializarDebounceCI(): void {
    this.ciInput$.pipe(
      debounceTime(1500),
      takeUntil(this.ciDestroy$),
    ).subscribe(ci => this.buscarPorCI(ci));
  }

  private buscarPorCI(ci: string): void {
    if (!ci || !this.form.ci || this.form.id) return;
    this.pessoaService.get(`/pessoas/buscar?ci=${ci}&categoriaFornecedor=true&size=1`).subscribe({
      next: (res: any) => {
        const resultados = res?.content || [];
        if (resultados.length > 0) {
          const pessoa = resultados[0];
          this.pessoaJaCadastradaId = pessoa.id ?? null;
          this.pessoaJaCadastradaMensagem = this.translate.instant('fornecedores.texts.pessoa_ja_cadastrada', { nome: pessoa.nome });
          this.pessoaJaCadastradaDialogVisible = true;
        }
      },
    });
  }

  editarPessoaJaCadastrada(): void {
    this.pessoaJaCadastradaDialogVisible = false;
    if (this.pessoaJaCadastradaId) {
      this.editarFornecedor(this.pessoaJaCadastradaId);
    }
  }

  // =========================================================================
  // Tabela
  // =========================================================================

  findFornecedores(
    page: number = 0,
    size: number = this.rows3,
    sort: string = 'id,asc',
  ): void {
    this.loading = true;
    const filter = `categoriaFornecedor=true`;

    this.pessoaService
      .get('/pessoas/buscar', {
        page,
        size,
        sort,
        filter: filter,
      }).subscribe({
        next: (response) => {
          this.pessoasOriginal = response.content || [];
          this.pessoas = [...this.pessoasOriginal];
          this.totalRecords = response.totalElements || this.pessoasOriginal.length;
          this.first3 = page * size;
          this.loading = false;

          // Reaplicar filtro local se houver termo de busca ativo
          if (this.searchNome && this.searchNome.trim().length > 0) {
            this.filtrarPessoasLocal(this.searchNome.trim());
          }
        },
        error: (error) => {
          console.error('Erro ao buscar fornecedores:', error);
          this.pessoasOriginal = [];
          this.pessoas = [];
          this.totalRecords = 0;
          this.loading = false;
        }
      });
  }

  onSearchChange(value: string): void {
    this.searchNome = value || '';
    const term = this.searchNome.trim();
    if (term.length >= 2) {
      this.searchSubject$.next(term);
    } else {
      // Menos de 2 caracteres: recarrega lista completa
      this.first3 = 0;
      this.findFornecedores(0, this.rows3);
    }
  }

  private buscarFornecedoresNoBackend(term: string): void {
    this.loading = true;
    this.first3 = 0;

    this.pessoaService
      .get('/pessoas/buscar', {
        page: 0,
        size: this.rows3,
        sort: `${this.lastfield},${this.lastSort}`,
        filter: `categoriaFornecedor=true&buscaGeral=${encodeURIComponent(term)}`,
      }).subscribe({
        next: (response) => {
          this.pessoasOriginal = response.content || [];
          this.pessoas = [...this.pessoasOriginal];
          this.totalRecords = response.totalElements || this.pessoasOriginal.length;
          this.loading = false;
        },
        error: (error) => {
          console.error('Erro ao buscar fornecedores:', error);
          this.pessoas = [];
          this.totalRecords = 0;
          this.loading = false;
        }
      });
  }

  /** Filtra pessoas localmente buscando em todas as colunas visiveis */
  private filtrarPessoasLocal(term: string): void {
    const termLower = term.toLowerCase();

    this.pessoas = this.pessoasOriginal.filter(pessoa => {
      if (this.isColumnVisible('codigoPessoa') && pessoa.codigoPessoa != null) {
        if (String(pessoa.codigoPessoa).includes(termLower)) return true;
      }
      if (this.isColumnVisible('nome') && pessoa.nome) {
        if (pessoa.nome.toLowerCase().includes(termLower)) return true;
      }
      if (this.isColumnVisible('fantasia') && pessoa.fantasia) {
        if (pessoa.fantasia.toLowerCase().includes(termLower)) return true;
      }
      if (this.isColumnVisible('telefone') && pessoa.telefone) {
        if (pessoa.telefone.toLowerCase().includes(termLower)) return true;
      }
      if (this.isColumnVisible('ci') && pessoa.ci) {
        if (String(pessoa.ci).toLowerCase().includes(termLower)) return true;
      }
      if (this.isColumnVisible('ruc') && pessoa.ruc) {
        if (pessoa.ruc.toLowerCase().includes(termLower)) return true;
      }
      if (this.isColumnVisible('cidade') && pessoa.cidade?.nome) {
        if (pessoa.cidade.nome.toLowerCase().includes(termLower)) return true;
      }
      if (this.isColumnVisible('criadoEm') && pessoa.criadoEm) {
        const dataFormatada = new Date(pessoa.criadoEm).toLocaleDateString('pt-BR');
        if (dataFormatada.toLowerCase().includes(termLower)) return true;
      }
      return false;
    });

    this.totalRecords = this.pessoas.length;
  }

  onPageChange3(event: any): void {
    const page = event.page;
    const size = event.rows;
    this.findFornecedores(page, size, `${this.lastfield},${this.lastSort}`);
  }

  onRowsPerPageChange(): void {
    // O (ngModelChange) do p-dropdown de "itens por pagina" tambem
    // dispara no bootstrap do PrimeNG com o valor atual, gerando uma chamada
    // duplicada de findFornecedores alem da feita em ngOnInit. Ignorar quando
    // "rows3" nao mudou de valor.
    if (this.rows3 === this._lastRows) return;
    this._lastRows = this.rows3;
    this.first3 = 0;
    this.findFornecedores(0, this.rows3, `${this.lastfield},${this.lastSort}`);
  }

  onSortChange(event: any): void {
    if (!event.field) return;

    const field = event.field;
    const order = event.order === 1 ? 'asc' : 'desc';

    // Evitar chamada duplicada se mesmo campo e mesma direcao
    if (this.lastfield === field && this.lastSort === order) return;

    this.lastfield = field;
    this.lastSort = order;

    // Ordenar no backend (todos os registros do banco)
    this.first3 = 0;
    const sort = `${field},${order}`;
    const term = this.searchNome?.trim();

    if (term && term.length >= 2) {
      this.loading = true;
      this.pessoaService
        .get('/pessoas/buscar', {
          page: 0,
          size: this.rows3,
          sort,
          filter: `categoriaFornecedor=true&buscaGeral=${encodeURIComponent(term)}`,
        }).subscribe({
          next: (response) => {
            this.pessoasOriginal = response.content || [];
            this.pessoas = [...this.pessoasOriginal];
            this.totalRecords = response.totalElements || this.pessoasOriginal.length;
            this.loading = false;
          },
          error: () => {
            this.loading = false;
          }
        });
    } else {
      this.findFornecedores(0, this.rows3, sort);
    }
  }

  private getSortValue(item: any, field: string): any {
    const parts = field.split('.');
    let value: any = item;
    for (const part of parts) {
      if (value == null) return null;
      value = value[part];
    }
    if (value != null && typeof value === 'object' && 'nome' in value) {
      return value.nome;
    }
    return value;
  }

  deletarFornecedor(id: string): void {
    // Modo embutido (aba do Operar Cliente, sob contexto do contador): exclusao fora de
    // escopo. Alem de o template ocultar o botao, bloqueamos aqui — a allowlist do contexto
    // nao tem DELETE nenhum, entao a chamada acabaria excluindo na empresa do PROPRIO
    // contador (o interceptor nao injeta o header em DELETE).
    if (this.embutido) {
      return;
    }
    this.deleteItemId = id;
    this.deleteDialogVisible = true;
  }

  confirmarExclusao(): void {
    if (!this.deleteItemId) return;

    this.pessoaService.delete(`/pessoas/${this.deleteItemId}`).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.texts.confirmacao,
          detail: this.texts.fornecedor_excluido_sucesso,
        });
        this.deleteDialogVisible = false;
        this.deleteItemId = null;
        this.findFornecedores(0, this.rows3, `${this.lastfield},${this.lastSort}`);
      },
      error: () => {
        // O HttpErrorInterceptor global já exibe o toast com a mensagem do backend.
        this.deleteDialogVisible = false;
        this.deleteItemId = null;
      },
    });
  }

  viewFornecedor(id: string): void {
    this.router.navigate(['/lista-de-fornecedores/view', id]);
  }

  formatarDataEHora(dataISO: string): string {
    const data = new Date(dataISO);
    if (isNaN(data.getTime())) {
      return '';
    }
    const dia = String(data.getDate()).padStart(2, '0');
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const ano = data.getFullYear();
    const horas = String(data.getHours()).padStart(2, '0');
    const minutos = String(data.getMinutes()).padStart(2, '0');
    return `${dia}/${mes}/${ano} ${horas}:${minutos}`;
  }

  // =========================================================================
  // Colunas
  // =========================================================================

  isColumnVisible(key: string): boolean {
    const col = this.columns.find(c => c.key === key);
    return col ? col.visible : true;
  }

  get visibleColumnCount(): number {
    return this.columns.filter(c => c.visible).length;
  }

  onColumnsChange(columns: TableColumn[]): void {
    this.columns = [...columns];
  }

  // =========================================================================
  // Acoes rapidas (menu popup)
  // =========================================================================

  toggleMenu(event: Event, pessoa: any): void {
    this.menuItems = this.buildMenuItems(pessoa);
    this.menuAcoes.toggle(event);
  }

  toggleMenuMobile(event: Event, pessoa: any): void {
    this.menuItemsMobile = this.buildMenuItemsMobile(pessoa);
    this.menuAcoesMobile.toggle(event);
  }

  private buildMenuItemsMobile(pessoa: any): MenuItem[] {
    return [
      {
        label: this.tooltips.editar,
        icon: 'pi pi-pencil',
        command: () => this.editarFornecedor(pessoa.id)
      },
      {
        label: this.tooltips.excluir,
        icon: 'pi pi-trash',
        styleClass: 'p-menuitem-danger',
        command: () => this.deletarFornecedor(pessoa.id)
      },
      { separator: true },
      {
        label: this.tooltips.adicionar_despesa,
        icon: 'pi pi-money-bill',
        command: () => this.adicionarDespesa(pessoa.id)
      },
      {
        label: this.tooltips.adicionar_receita,
        icon: 'pi pi-dollar',
        command: () => this.adicionarReceita(pessoa.id)
      },
      {
        label: this.tooltips.ver_pedidos,
        icon: 'pi pi-shopping-bag',
        command: () => this.verCompras(pessoa.id)
      },
      {
        label: this.tooltips.ver_itens,
        icon: 'pi pi-box',
        command: () => this.verItens(pessoa.id)
      },
      {
        label: this.tooltips.ver_titulos_abertos,
        icon: 'pi pi-list',
        command: () => this.verTitulosAbertos(pessoa)
      }
    ];
  }

  private buildMenuItems(pessoa: any): MenuItem[] {
    return [
      {
        label: this.tooltips.adicionar_despesa,
        icon: 'pi pi-money-bill',
        command: () => this.adicionarDespesa(pessoa.id)
      },
      {
        label: this.tooltips.adicionar_receita,
        icon: 'pi pi-dollar',
        command: () => this.adicionarReceita(pessoa.id)
      },
      {
        label: this.tooltips.ver_pedidos,
        icon: 'pi pi-shopping-bag',
        command: () => this.verCompras(pessoa.id)
      },
      {
        label: this.tooltips.ver_itens,
        icon: 'pi pi-box',
        command: () => this.verItens(pessoa.id)
      },
      {
        label: this.tooltips.ver_titulos_abertos,
        icon: 'pi pi-list',
        command: () => this.verTitulosAbertos(pessoa)
      }
    ];
  }

  adicionarDespesa(pessoaId: number): void {
    this.router.navigate(['/nova-despesa'], { queryParams: { pessoaId } });
  }

  adicionarReceita(pessoaId: number): void {
    this.router.navigate(['/nova-receita'], { queryParams: { pessoaId } });
  }

  verCompras(pessoaId: number): void {
    this.router.navigate(['/compras-list'], { queryParams: { pessoaId } });
  }

  verItens(pessoaId: number): void {
    this.router.navigate(['/produtos'], { queryParams: { fornecedorId: pessoaId } });
  }

  verTitulosAbertos(pessoa: any): void {
    this.router.navigate(['/contas-a-pagar/pessoa', pessoa.id], {
      queryParams: { nome: pessoa.nome || '', origem: 'fornecedores', tipo: 'SAIDA' },
    });
  }

  private loadColumnPreferences(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const visibleKeys = JSON.parse(stored) as string[];
        this.columns.forEach(col => {
          if (!col.fixed) {
            col.visible = visibleKeys.includes(col.key);
          }
        });
      }
    } catch (e) {
      console.warn('Erro ao carregar preferencias de colunas:', e);
    }
  }
}
